import { useState, useEffect, useCallback, useRef } from "react";
import adminServices from "../../services/adminServices";
import Page from '../../components/Page';

const STATUS_OPTIONS = [
  { value: "all", label: "All users" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100, 250, 500, 1000, 2000];
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
};

const formatDate = (value) => {
  if (!value) return "NA";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

function StatusPill({ status }) {
  const styles = {
    Active: { bg: "#EAF6EE", fg: "#1E7A3D", dot: "#2FA84F" },
    Inactive: { bg: "#F3F1EE", fg: "#6B6459", dot: "#9C9184" },
    Suspended: { bg: "#FBEAEA", fg: "#B3261E", dot: "#D6423B" },
  };
  const s = styles[status] || styles.Inactive;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 500,
        background: s.bg,
        color: s.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: s.dot,
          display: "inline-block",
        }}
      />
      {status}
    </span>
  );
}

function initials(firstName, lastName, email) {
  const f = (firstName || "").trim();
  const l = (lastName || "").trim();
  if (f || l) return `${f[0] || ""}${l[0] || ""}`.toUpperCase();
  return (email || "?")[0].toUpperCase();
}

function Checkbox({ checked, indeterminate, onChange, ariaLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      style={{ width: 16, height: 16, cursor: "pointer" }}
    />
  );
}

/* ---------- Shared toolbar styles (everything is 38px tall) ---------- */
const CONTROL_HEIGHT = 38;

const segmentedStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "#F3F1EE",
  padding: 3,
  borderRadius: 10,
  height: CONTROL_HEIGHT,
  boxSizing: "border-box",
};

const segButtonStyle = (active) => ({
  border: "none",
  cursor: "pointer",
  padding: "0 14px",
  height: CONTROL_HEIGHT - 6,
  borderRadius: 7,
  fontSize: 13.5,
  fontWeight: 500,
  whiteSpace: "nowrap",
  background: active ? "#FFFFFF" : "transparent",
  color: active ? "#22201B" : "#6B6459",
  boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
  transition: "all 120ms ease",
});

const dateFieldStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  height: CONTROL_HEIGHT,
  boxSizing: "border-box",
  padding: "0 10px 0 12px",
  border: "1px solid #E1DDD5",
  borderRadius: 10,
  background: "#fff",
  fontSize: 13.5,
  fontWeight: 500,
  color: "#6B6459",
  whiteSpace: "nowrap",
};

const dateInputStyle = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 13.5,
  fontFamily: "inherit",
  color: "#22201B",
  padding: 0,
};

const outlineButtonStyle = (disabled) => ({
  height: CONTROL_HEIGHT,
  boxSizing: "border-box",
  padding: "0 14px",
  borderRadius: 10,
  border: "1px solid #E1DDD5",
  background: "#fff",
  fontSize: 13.5,
  fontWeight: 500,
  color: "#22201B",
  whiteSpace: "nowrap",
  cursor: disabled ? "default" : "pointer",
});

export default function EngagementUsersList() {
  const [status, setStatus] = useState("all");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [query, setQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [previewQuery, setPreviewQuery] = useState("");

  const today = toISODate(new Date());
  const rangeInvalid = !!fromDate && !!toDate && fromDate > toDate;

  useEffect(() => {
    const next = query.trim();
    if (next === debouncedSearch) return undefined;
    const t = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, debouncedSearch]);

  const fetchUsers = useCallback(async () => {
    if (rangeInvalid) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await adminServices.getUsersByStatus(
        status,
        fromDate,
        toDate,
        page,
        pageSize,
          encodeURIComponent(debouncedSearch)
      );
      const list = res.data.users || [];
      const serverPagination = res.data.pagination;

      setUsers(list);
      setPagination(
        serverPagination || {
          page: 1,
          pageSize,
          total: list.length,
          totalPages: 1,
        }
      );

      const serverSize = serverPagination?.pageSize;
      if (serverSize && serverSize < pageSize) {
        setNotice({
          type: "error",
          text: `The server limits results to ${serverSize} per page, so ${pageSize} rows can't be shown at once.`,
        });
      }
    } catch (err) {
      setError(err.message || "Something went wrong while loading users.");
      setUsers([]);
      setPagination({ page: 1, pageSize, total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [status, fromDate, toDate, page, pageSize, rangeInvalid, debouncedSearch]);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const res = await adminServices.getCrisisTemplate();
      const data = res.data || {};
      setTemplates(data);
    } catch (err) {
      setTemplatesError(err.message || "Couldn't load templates.");
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [users]);

  useEffect(() => {
    if (!showPreview) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !sending) {
        setShowPreview(false);
        setPreviewQuery("");
        setPreviewUsers([]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPreview, sending]);

  useEffect(() => {
    if (notice?.type !== "success") return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const changeStatus = (v) => {
    setPage(1);
    setStatus(v);
    setQuery("");
    if (v === "inactive") {
      setFromDate("");
      setToDate("");
    }
  };
  const changeFrom = (v) => { setPage(1); setFromDate(v); setQuery(""); };
  const changeTo = (v) => { setPage(1); setToDate(v); setQuery(""); };
  const changePageSize = (v) => { setPage(1); setPageSize(v); };
  const applyPreset = (days) => {
    setPage(1);
    setFromDate(daysAgo(days));
    setToDate(today);
    setQuery("");
  };
  const clearDates = () => {
    setDebouncedSearch('');
    setPage(1);
    setFromDate("");
    setToDate("");
    setQuery("");
  };

  const filteredUsers = users;

  const allFilteredEmails = filteredUsers.map((u) => u.email);
  const selectedInViewCount = allFilteredEmails.filter((email) => selectedIds.has(email)).length;
  const allSelected = allFilteredEmails.length > 0 && selectedInViewCount === allFilteredEmails.length;
  const someSelected = selectedInViewCount > 0 && !allSelected;

  const toggleOne = (email) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allFilteredEmails.forEach((email) => next.delete(email));
      } else {
        allFilteredEmails.forEach((email) => next.add(email));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const closePreview = () => {
    setShowPreview(false);
    setPreviewQuery("");
    setPreviewUsers([]);
  };

  const openPreview = () => {
    if (!selectedTemplateName) {
      setNotice({ type: "error", text: "Please select an email template." });
      return;
    }
    if (selectedIds.size === 0) {
      setNotice({ type: "error", text: "Please select at least one user." });
      return;
    }
    setNotice(null);
    setPreviewUsers(users.filter((u) => selectedIds.has(u.email)));
    setPreviewQuery("");
    setShowPreview(true);
  };

 const previewTemplateLabel =
  (Array.isArray(templates) &&
    templates.find((t) => t.mail_template_type === selectedTemplateName)?.title) ||
  selectedTemplateName;


  const previewFiltered = previewUsers.filter((u) => {
    if (!previewQuery.trim()) return true;
    const q = previewQuery.trim().toLowerCase();
    const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
    return name.includes(q) || (u.email || "").toLowerCase().includes(q);
  });
  const previewCheckedCount = previewFiltered.filter((u) => selectedIds.has(u.email)).length;
  const previewAllChecked = previewFiltered.length > 0 && previewCheckedCount === previewFiltered.length;
  const previewSomeChecked = previewCheckedCount > 0 && !previewAllChecked;

  const togglePreviewAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      previewFiltered.forEach((u) => {
        if (previewAllChecked) next.delete(u.email);
        else next.add(u.email);
      });
      return next;
    });
  };

  const SendEmail = async () => {
    if (!selectedTemplateName) {
      setNotice({ type: "error", text: "Please select an email template." });
      return;
    }

    if (selectedIds.size === 0) {
      setNotice({ type: "error", text: "Please select at least one user." });
      return;
    }

    setSending(true);
    setNotice(null);
    try {
      const count = selectedIds.size;
      await adminServices.sendCrisisEmail(selectedTemplateName, Array.from(selectedIds));
      setNotice({
        type: "success",
        text: `Email sent successfully to ${count} user${count === 1 ? "" : "s"}.`,
      });
      clearSelection();
      setSelectedTemplateName("");
      closePreview();
    } catch (error) {
      console.error("Error sending email:", error);
      setNotice({ type: "error", text: "Failed to send email. Please try again." });
    } finally {
      setSending(false);
    }
  };

  const canSend = !sending && !!selectedTemplateName && selectedIds.size > 0;
  const canGoPrev = !loading && pagination.page > 1;
  const canGoNext = !loading && pagination.page < pagination.totalPages;

  function renderTableBody({ loading, filteredUsers, selectedIds, toggleOne }) {
    if (loading) {
      return (
        <tr>
          <td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#9C9184", fontSize: 14 }}>
            Loading users…
          </td>
        </tr>
      );
    }

    if (rangeInvalid) {
      return (
        <tr>
          <td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#B3261E", fontSize: 14 }}>
            "From" date must be on or before "To" date.
          </td>
        </tr>
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <tr>
          <td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#9C9184", fontSize: 14 }}>
            {debouncedSearch
              ? `No users found for "${debouncedSearch}".`
              : "No users match the current filters."}
          </td>
        </tr>
      );
    }

    return filteredUsers.map((u, i) => {
      const isSelected = selectedIds.has(u.email);
      return (
        <tr
          key={u.id}
          style={{
            background: isSelected ? "#F5F8FF" : "transparent",
            borderBottom: i === filteredUsers.length - 1 ? "none" : "1px solid #EFECE6",
          }}
        >
          <td style={{ padding: "10px 16px" }}>
            <Checkbox
              checked={isSelected}
              onChange={() => toggleOne(u.email)}
              ariaLabel={`Select ${u.firstName || u.email || u.id}`}
            />
          </td>
          <td style={{ padding: "10px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background: "#EFECE6",
                  color: "#6B6459",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {initials(u.firstName, u.lastName, u.email)}
              </div>
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {u.firstName || u.lastName
                  ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
                  : "—"}
              </span>
            </div>
          </td>
          <td style={{ padding: "10px 16px", fontSize: 14, color: "#413D36" }}>
            {u.email || "—"}
          </td>
          <td style={{ padding: "10px 16px", fontSize: 14, color: "#413D36", whiteSpace: "nowrap" }}>
            {formatDate(u.createDateTime)}
          </td>
          <td style={{ padding: "10px 16px", fontSize: 14, color: "#413D36", whiteSpace: "nowrap" }}>
            {formatDate(u.lastLogin)}
          </td>
          <td style={{ padding: "10px 16px" }}>
            <StatusPill status={u.status} />
          </td>
        </tr>
      );
    });
  }

  return (
    <Page title={"Course-details"}>
      <div
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif',
          color: "#22201B",
          width: "100%",
          maxWidth: "none",
          boxSizing: "border-box",
          padding: 24,
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>User Engagement</h1>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 12,
            alignItems: "center",
          }}
        >
          <div style={segmentedStyle} role="group" aria-label="User status">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => changeStatus(opt.value)}
                style={segButtonStyle(status === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {status !== "inactive" && (
            <>
              <label htmlFor="from-date" style={dateFieldStyle}>
                <span>Last Joined from</span>
                <input
                  id="from-date"
                  type="date"
                  value={fromDate}
                  max={toDate || today}
                  onChange={(e) => changeFrom(e.target.value)}
                  style={dateInputStyle}
                />
              </label>

              <label htmlFor="to-date" style={dateFieldStyle}>
                <span>to</span>
                <input
                  id="to-date"
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  max={today}
                  onChange={(e) => changeTo(e.target.value)}
                  style={dateInputStyle}
                />
              </label>

              <div style={segmentedStyle} role="group" aria-label="Quick date ranges">
                {PRESETS.map((p) => (
                  <button
                    key={p.days}
                    onClick={() => applyPreset(p.days)}
                    style={segButtonStyle(fromDate === daysAgo(p.days) && toDate === today)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </>
          )}

          <button onClick={clearDates} style={outlineButtonStyle(false)}>
            Clear Filters
          </button>
        </div>

        {/* Search + refresh */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 18,
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder="Search name or email"
            aria-label="Search name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: "1 1 240px",
              height: CONTROL_HEIGHT,
              boxSizing: "border-box",
              padding: "0 12px",
              borderRadius: 10,
              border: "1px solid #E1DDD5",
              fontSize: 13.5,
              minWidth: 200,
            }}
          />

          <button onClick={fetchUsers} disabled={loading} style={outlineButtonStyle(loading)}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {notice && !showPreview && (
          <div
            role={notice.type === "error" ? "alert" : "status"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 14px",
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 13.5,
              fontWeight: 500,
              background: notice.type === "success" ? "#EAF6EE" : "#FBEAEA",
              color: notice.type === "success" ? "#1E7A3D" : "#B3261E",
              border: `1px solid ${notice.type === "success" ? "#CFE8D7" : "#F3CFCD"}`,
            }}
          >
            <span>{notice.text}</span>
            <button
              onClick={() => setNotice(null)}
              aria-label="Dismiss message"
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
                color: "inherit",
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        {selectedIds.size > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              padding: "12px 16px",
              borderRadius: 10,
              background: "#EFF4FF",
              border: "1px solid #DCE6FF",
              marginBottom: 16,
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1E3A8A", whiteSpace: "nowrap" }}>
              {selectedIds.size} user{selectedIds.size === 1 ? "" : "s"} selected
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <select
                value={selectedTemplateName}
                onChange={(e) => setSelectedTemplateName(e.target.value)}
                disabled={templatesLoading || templates.length === 0 || sending}
                aria-label="Email template"
                style={{
                  height: 34,
                  padding: "0 10px",
                  borderRadius: 8,
                  border: "1px solid #C7D2FE",
                  background: "#fff",
                  fontSize: 13.5,
                  color: "#1E3A8A",
                  minWidth: 200,
                  cursor: templatesLoading || templates.length === 0 || sending ? "default" : "pointer",
                }}
              >
                <option value="" disabled>
                  Select template
                </option>

                {templatesLoading && <option value="">Loading templates…</option>}

                {!templatesLoading && templates.length === 0 && (
                  <option value="">No templates available</option>
                )}

                {!templatesLoading &&
                  templates?.length > 0 &&
                  templates.map((t) => (
                    <option key={t.mail_template_type} value={t.mail_template_type}>
                      {t?.title}
                    </option>
                  ))}
              </select>

              <div style={{ width: 1, height: 20, background: "#C7D2FE" }} />

              <button
                onClick={clearSelection}
                disabled={sending}
                style={{
                  height: 34,
                  padding: "0 10px",
                  border: "none",
                  background: "none",
                  color: "#1E3A8A",
                  fontSize: 13.5,
                  fontWeight: 500,
                  cursor: sending ? "default" : "pointer",
                  opacity: sending ? 0.5 : 1,
                }}
              >
                Clear
              </button>

              <button
                onClick={openPreview}
                disabled={!canSend}
                style={{
                  height: 34,
                  padding: "0 18px",
                  borderRadius: 8,
                  border: "none",
                  background: canSend ? "#1E3A8A" : "#AEB9DC",
                  color: "#fff",
                  fontSize: 13.5,
                  fontWeight: 600,
                  cursor: canSend ? "pointer" : "default",
                  transition: "background 0.15s ease",
                }}
              >
                Review &amp; send
              </button>
            </div>
          </div>
        )}

        {/* Template load error */}
        {templatesError && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#FBEAEA",
              color: "#B3261E",
              fontSize: 13.5,
              marginBottom: 14,
            }}
          >
            Couldn't load templates: {templatesError}
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#FBEAEA",
              color: "#B3261E",
              fontSize: 13.5,
              marginBottom: 14,
            }}
          >
            Couldn't load users: {error}
          </div>
        )}

        {/* Table */}
        <div
          style={{
            border: "1px solid #E1DDD5",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#FAF9F6" }}>
                  <th
                    style={{
                      textAlign: "left",
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#6B6459",
                      padding: "10px 16px",
                      borderBottom: "1px solid #E1DDD5",
                      width: 36,
                    }}
                  >
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleSelectAll}
                      ariaLabel="Select all users on this page"
                    />
                  </th>
                  {["User", "Email", "Joined", "Last Joined", "Status"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "#6B6459",
                        padding: "10px 16px",
                        borderBottom: "1px solid #E1DDD5",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderTableBody({ loading, filteredUsers, selectedIds, toggleOne })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          {pagination.total > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderTop: "1px solid #E1DDD5",
                background: "#FAF9F6",
                fontSize: 13,
                color: "#6B6459",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span>
                  Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
                </span>
                <label htmlFor="rows-per-page" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  Rows per page
                  <select
                    id="rows-per-page"
                    value={pageSize}
                    onChange={(e) => changePageSize(Number(e.target.value))}
                    disabled={loading}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "1px solid #E1DDD5",
                      fontSize: 13,
                      color: "#22201B",
                      background: "#fff",
                      cursor: loading ? "default" : "pointer",
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {pagination.totalPages > 1 && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!canGoPrev}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid #E1DDD5",
                      background: "#fff",
                      fontSize: 13,
                      cursor: canGoPrev ? "pointer" : "default",
                      opacity: canGoPrev ? 1 : 0.5,
                    }}
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={!canGoNext}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid #E1DDD5",
                      background: "#fff",
                      fontSize: 13,
                      cursor: canGoNext ? "pointer" : "default",
                      opacity: canGoNext ? 1 : 0.5,
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preview before sending */}
        {showPreview && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(20,18,14,0.5)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="preview-title"
              style={{
                background: "#fff",
                borderRadius: 14,
                width: "100%",
                maxWidth: 760,
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  borderBottom: "1px solid #E1DDD5",
                }}
              >
                <h2 id="preview-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Review before sending
                </h2>
                <button
                  onClick={closePreview}
                  disabled={sending}
                  aria-label="Close preview"
                  style={{
                    border: "none",
                    background: "none",
                    fontSize: 24,
                    lineHeight: 1,
                    cursor: sending ? "default" : "pointer",
                    color: "#6B6459",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Summary */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  padding: "16px 20px 0",
                }}
              >
                <div
                  style={{
                    flex: "1 1 200px",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "#EFF4FF",
                    border: "1px solid #DCE6FF",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B6459" }}>Learners selected</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: "#1E3A8A" }}>
                    {selectedIds.size}
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#6B6459" }}>
                      {" "}of {previewUsers.length}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    flex: "2 1 260px",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "#FAF9F6",
                    border: "1px solid #E1DDD5",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B6459" }}>Email template</div>
                  <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, wordBreak: "break-word" }}>
                    {previewTemplateLabel || "—"}
                  </div>
                </div>
              </div>

              {/* Search */}
              <div style={{ padding: "16px 20px 10px" }}>
                <input
                  type="text"
                  placeholder="Search selected learners by name or email"
                  aria-label="Search selected learners"
                  value={previewQuery}
                  onChange={(e) => setPreviewQuery(e.target.value)}
                  style={{
                    width: "100%",
                    height: CONTROL_HEIGHT,
                    boxSizing: "border-box",
                    padding: "0 12px",
                    borderRadius: 10,
                    border: "1px solid #E1DDD5",
                    fontSize: 13.5,
                  }}
                />
              </div>

              {/* List */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  margin: "0 20px",
                  border: "1px solid #E1DDD5",
                  borderRadius: 10,
                  minHeight: 120,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "8px 14px",
                    background: "#FAF9F6",
                    borderBottom: "1px solid #E1DDD5",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#6B6459",
                    position: "sticky",
                    top: 0,
                  }}
                >
                  <Checkbox
                    checked={previewAllChecked}
                    indeterminate={previewSomeChecked}
                    onChange={togglePreviewAll}
                    ariaLabel="Select or unselect all listed learners"
                  />
                  <span>{previewFiltered.length} listed · uncheck to remove from this send</span>
                </div>

                {previewFiltered.length === 0 ? (
                  <div style={{ padding: 28, textAlign: "center", color: "#9C9184", fontSize: 14 }}>
                    No selected learners match your search.
                  </div>
                ) : (
                  previewFiltered.map((u, i) => {
                    const checked = selectedIds.has(u.email);
                    return (
                      <div
                        key={u.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "9px 14px",
                          borderBottom:
                            i === previewFiltered.length - 1 ? "none" : "1px solid #EFECE6",
                          opacity: checked ? 1 : 0.5,
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleOne(u.email)}
                          ariaLabel={`Include ${u.firstName || u.email}`}
                        />
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background: "#EFECE6",
                            color: "#6B6459",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {initials(u.firstName, u.lastName, u.email)}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>
                            {u.firstName || u.lastName
                              ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
                              : "—"}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#6B6459",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {u.email || "—"}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Error inside modal */}
              {notice?.type === "error" && (
                <div
                  role="alert"
                  style={{
                    margin: "12px 20px 0",
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: "#FBEAEA",
                    color: "#B3261E",
                    fontSize: 13.5,
                    fontWeight: 500,
                  }}
                >
                  {notice.text}
                </div>
              )}

              {/* Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  padding: "16px 20px",
                }}
              >
                <button onClick={closePreview} disabled={sending} style={outlineButtonStyle(sending)}>
                  Cancel
                </button>
                <button
                  onClick={SendEmail}
                  disabled={sending || selectedIds.size === 0}
                  style={{
                    height: CONTROL_HEIGHT,
                    padding: "0 20px",
                    borderRadius: 10,
                    border: "none",
                    background: sending || selectedIds.size === 0 ? "#AEB9DC" : "#1E3A8A",
                    color: "#fff",
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: sending || selectedIds.size === 0 ? "default" : "pointer",
                  }}
                >
                  {sending
                    ? "Sending…"
                    : `Send to ${selectedIds.size} learner${selectedIds.size === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}